import FJConfig from '../config/FJConfig';
import {
    GENDER_OPTIONS,
    PROFESSION_OPTIONS,
    HAIR_STYLES_PREFERENCES,
    CLOTHING_PREFERENCES,
    BACKGROUND_PREFERENCES,
    THEME_OPTIONS,
    DEFAULT_MALE_CONFIG,
    DEFAULT_FEMALE_CONFIG,
    DEFAULT_CONFIG,
    SHOT_OPTIONS,
    HAIR_COLOR_NO_CHANGE,
    HAIR_STYLE_NO_CHANGE,
    AGE_OPTIONS,
} from '../config/dataConfig/FJHeadshotGeneratorData';
import FJVersionConfig from '../config/FJVersionConfig';

const ExtraPrompt = 'African descent with obsidian skin, rich chocolate complexion.';
const AgeMap = {
    '0-2': 'little baby',
    '2-3': '2-3 years old',
    '3-4': '3-4 years old',
    '5-6': '5-6 years old',
    '7-8': '7-8 years old',
    '8-10': '8-10 years old',
    '10-13': '10-13 years old',
};
/**
 * 根据年龄范围生成年龄描述
 */
const getAgeDescription = (originalPrompt, ageRange) => {
    const ageDescription = AgeMap[ageRange] || 'child';
    return originalPrompt.replace('xxx', ageDescription);
};

const FJHeadshotUtil = {
    getHeadshotListAndInfo: function (list, resultFunc, originalFunc) {
        const headshotList =
            list
                ?.map(item => {
                    const info = JSON.parse(item.info || '{}');
                    return {
                        id: item.id,
                        eventId: item.event_id,
                        ...info,
                        imgUrl: resultFunc(item.result),
                        originalUrl: originalFunc(item.original),
                        is_grayscale: !!item.is_grayscale,
                    };
                })
                ?.sort((a, b) => {
                    // 按照prompt排序，相同prompt的项目在一起
                    const promptA = a.prompt || '';
                    const promptB = b.prompt || '';
                    return promptA.localeCompare(promptB);
                }) ?? [];
        return [
            headshotList,
            {
                job_type: list[0]?.job_type,
                created_at: list[0]?.created_at,
                job_state: list[0]?.job_state,
            },
        ];
    },

    /**
     * 从数组中随机选择多个不重复的提示词
     */
    _getRandomPrompts: function (prompts, count) {
        if (!prompts || prompts.length === 0) return [];
        if (count >= prompts.length) {
            // 如果需要的数量大于等于总数，返回所有并打乱
            return [...prompts].sort(() => Math.random() - 0.5);
        }

        // 随机选择指定数量的不重复提示词
        const shuffled = [...prompts].sort(() => Math.random() - 0.5);
        return shuffled.slice(0, count);
    },

    /**
     * 根据背景数量计算每个背景的提示词数量
     */
    _calculatePromptCounts: function (backgroundCount, totalPrompts = 20) {
        if (backgroundCount <= 0) return [];
        if (backgroundCount === 1) return [totalPrompts];
        if (backgroundCount === 2) return [10, 10];
        if (backgroundCount === 3) return [7, 7, 6];
        if (backgroundCount === 4) return [5, 5, 5, 5];

        // 如果超过4个背景，平均分配
        const baseCount = Math.floor(totalPrompts / backgroundCount);
        const remainder = totalPrompts % backgroundCount;

        const counts = [];
        for (let i = 0; i < backgroundCount; i++) {
            counts.push(baseCount + (i < remainder ? 1 : 0));
        }

        return counts;
    },

    /**
     * 批量生成头像提示词（生成20条不同的提示词）
     */
    _generateBatchHeadshotPrompts: function (
        gender,
        category,
        shot,
        hairColor,
        hairStyle,
        clothes,
        background,
        count = 20,
        dataSet,
        race,
    ) {
        try {
            // 加载各种提示词数据
            const {
                maleHairstyleData,
                backgroundData,
                shotData,
                maleClothesData,
                femaleHairstyleData,
                femaleClothesData,
                otherData,
            } = dataSet;
            if (
                !maleHairstyleData ||
                !backgroundData ||
                !shotData ||
                !maleClothesData ||
                !femaleHairstyleData ||
                !femaleClothesData ||
                !otherData
            ) {
                throw new Error('missing prompt data');
            }

            const prompts = [];

            // 获取发型提示词池
            const hairStylePrompts =
                hairStyle === HAIR_STYLE_NO_CHANGE
                    ? ['Keep the original hairstyle']
                    : gender === GENDER_OPTIONS.MALE
                      ? maleHairstyleData[hairStyle]
                      : femaleHairstyleData[hairStyle];

            // 获取背景提示词池
            const backgroundPrompts = backgroundData[background];

            // 获取拍摄角度提示词池
            const shotPrompts = shotData[shot];

            // 构建服装提示词池（可以扩展为更多变体）
            const clothingPrompts =
                gender === GENDER_OPTIONS.MALE ? maleClothesData[shot][clothes] : femaleClothesData[shot][clothes];

            // 生成指定数量的提示词
            for (let i = 0; i < count; i++) {
                // 从每个池中随机选择一个提示词
                const hairStylePrompt = this._getRandomPrompts(hairStylePrompts, 1)[0];
                const backgroundPrompt = this._getRandomPrompts(backgroundPrompts, 1)[0];
                const shotPrompt = this._getRandomPrompts(shotPrompts, 1)[0];
                const clothingPrompt = this._getRandomPrompts(clothingPrompts, 1)[0];

                // 组合提示词
                const finalPrompt = `Create an image of a professional photography.  Use the attached photo as a reference for the face. ${hairStylePrompt}${hairColor === HAIR_COLOR_NO_CHANGE ? '' : `, and the hair color is ${hairColor}`}. ${clothingPrompt} ${backgroundPrompt} ${shotPrompt} ${race ? ExtraPrompt : ''}`;
                prompts.push({
                    value: finalPrompt.trim(),
                    is_grayscale: false,
                });
            }

            return prompts;
        } catch (error) {
            console.error(error);
            throw error;
        }
    },

    generateHeadshotPrompt: async function ({
        gender,
        category,
        shot,
        hairColor,
        hairStyle,
        clothes,
        background,
        theme,
        dataSet,
        race,
        age,
    }) {
        // 如果是专业头像，根据背景数量生成提示词
        if (category === 'professional') {
            const backgroundCount = background.length;
            const promptCounts = this._calculatePromptCounts(backgroundCount, 20);

            const allPrompts = [];

            for (let i = 0; i < background.length; i++) {
                let _background = background[i];
                const count = promptCounts[i];

                const backgroundPrompts = this._generateBatchHeadshotPrompts(
                    gender,
                    category,
                    shot,
                    hairColor,
                    hairStyle,
                    clothes,
                    _background,
                    count,
                    dataSet,
                    race,
                );

                allPrompts.push(...backgroundPrompts);
            }

            return allPrompts;
        } else {
            // 根据选择的category，theme，gender，index 匹配提示词
            const themePrompts = [];
            const { otherData } = dataSet;
            if (!otherData) {
                throw new Error('missing prompt data');
            }
            theme.forEach(themeItem => {
                // 获取主题的提示词数据
                const themeData = dataSet.otherData[themeItem.value];
                if (themeData && themeData[gender]) {
                    // 根据index获取对应的提示词
                    const promptIndex = themeItem.index || 0;
                    let prompt = themeData[gender][promptIndex];
                    if (race) {
                        prompt = `${prompt} ${ExtraPrompt}`;
                    }

                    // 如果是儿童分类，添加年龄信息
                    if (category.includes('kid') && age) {
                        prompt = getAgeDescription(prompt, age, gender);
                    }

                    if (prompt) {
                        // 同一个提示词复制3份
                        for (let i = 0; i < 3; i++) {
                            themePrompts.push({
                                value: prompt,
                                is_grayscale:
                                    themeItem.value === 'magazine'
                                        ? gender === GENDER_OPTIONS.MALE
                                            ? promptIndex < 7
                                            : promptIndex < 5
                                        : false,
                            });
                        }
                    }
                }
            });

            return themePrompts;
        }
    },

    _fetchHeadshotPromptData: async path => {
        const response = await fetch(FJConfig.presetListJsonPath + path);
        const data = await response.json();
        return data;
    },

    loadHeadshotPromptData: async () => {
        let dataSet = {};
        try {
            const maleHairstyleData = await FJHeadshotUtil._fetchHeadshotPromptData(
                `male_hairstyle.json?v=${FJVersionConfig.staticPathVersion}`,
            );
            const maleClothesData = await FJHeadshotUtil._fetchHeadshotPromptData(
                `new_male_clothes.json?v=${FJVersionConfig.staticPathVersion}`,
            );
            const backgroundData = await FJHeadshotUtil._fetchHeadshotPromptData(
                `background.json?v=${FJVersionConfig.staticPathVersion}`,
            );
            const shotData = await FJHeadshotUtil._fetchHeadshotPromptData(
                `shot.json?v=${FJVersionConfig.staticPathVersion}`,
            );
            const femaleHairstyleData = await FJHeadshotUtil._fetchHeadshotPromptData(
                `female_hairstyle.json?v=${FJVersionConfig.staticPathVersion}`,
            );
            const femaleClothesData = await FJHeadshotUtil._fetchHeadshotPromptData(
                `new_female_clothes.json?v=${FJVersionConfig.staticPathVersion}`,
            );

            const otherData = await FJHeadshotUtil._fetchHeadshotPromptData(
                `other_themes.json?v=${FJVersionConfig.staticPathVersion}`,
            );

            dataSet = {
                maleHairstyleData,
                maleClothesData,
                backgroundData,
                shotData,
                femaleHairstyleData,
                femaleClothesData,
                otherData,
            };
            return dataSet;
        } catch (error) {
            console.error(error);
            return null;
        }
    },

    /**
     * 根据性别获取默认配置
     */
    getDefaultConfigByGender: function (gender) {
        switch (gender) {
            case GENDER_OPTIONS.MALE:
                return DEFAULT_MALE_CONFIG;
            case GENDER_OPTIONS.FEMALE:
                return DEFAULT_FEMALE_CONFIG;
            default:
                return DEFAULT_CONFIG;
        }
    },

    /**
     * 获取特定性别的职业选项
     */
    getProfessionsByGender: function (gender) {
        return Object.values(PROFESSION_OPTIONS).filter(profession => profession.gender === gender);
    },

    /**
     * 获取特定性别的发型选项
     */
    getHairStylesByGender: function (gender) {
        return HAIR_STYLES_PREFERENCES.filter(style => style.gender === gender);
    },

    /**
     * 获取特定性别的服装选项
     */
    getClothingByGender: function (gender) {
        return CLOTHING_PREFERENCES.filter(clothing => clothing.gender === gender);
    },

    /**
     * 获取特定性别的背景选项
     */
    getBackgroundsByGender: function (gender) {
        return BACKGROUND_PREFERENCES.filter(background => background.gender === gender);
    },

    getShotByGender: function (gender) {
        return SHOT_OPTIONS.filter(shot => shot.gender === gender);
    },

    /**
     * 重置为指定性别的默认值
     */
    resetToGenderDefaults: function (gender, selectedCategoryKey, currentConfig = {}) {
        const defaultConfig = this.getDefaultConfigByGender(gender);
        const newProfession = this.getProfessionsByGender(gender)[0];

        const firstTheme = this.getThemesByProfessionAndGender(newProfession, selectedCategoryKey, gender);
        let age = null;

        if (selectedCategoryKey.includes('kid')) {
            age = AGE_OPTIONS[0];
        }

        return {
            ...currentConfig,
            gender: defaultConfig.gender,
            profession: newProfession,
            hairPreferences: {
                style: this.getHairStylesByGender(gender)[0].value,
                color: {
                    key: HAIR_COLOR_NO_CHANGE,
                    value: HAIR_COLOR_NO_CHANGE,
                },
            },
            clothingPreference: this.getClothingByGender(gender)[0],
            backgroundPreference: [this.getBackgroundsByGender(gender)[0]],
            theme: firstTheme.slice(0, 1),
            ratio: defaultConfig.ratio,
            shot: this.getShotByGender(gender)[1],
            age: age,
        };
    },

    /**
     * 根据职业、分类和性别获取可用的主题选项
     */
    getThemesByProfessionAndGender: function (profession, categoryKey, gender) {
        // 当职业和分类都为空，或者性别为空，或者分类为专业头像，返回空数组
        if ((!profession && !categoryKey) || !gender || categoryKey === 'professional') {
            return [];
        }

        // 根据分类和职业确定可用的主题
        if (categoryKey === 'occupational') {
            const selectedProfessionValue = profession?.value;

            const path = THEME_OPTIONS[selectedProfessionValue]?.path;
            const length = THEME_OPTIONS[selectedProfessionValue][gender] ?? 0;
            return Array.from({ length }, (_, index) => ({
                imageUrl: `${path}/${gender}_${path}_${(index + 1).toString().padStart(2, '0')}.png`,
                key: `${gender}-${path}-${index + 1}`,
                value: `${path}`,
                index: index,
            }));
        } else {
            const path = THEME_OPTIONS[categoryKey]?.path;
            const length = THEME_OPTIONS[categoryKey][gender] ?? 0;
            if (categoryKey.includes('kid')) {
                gender = gender === GENDER_OPTIONS.MALE ? 'boy' : 'girl';
            }
            return Array.from({ length }, (_, index) => ({
                imageUrl: `${path}/${gender}_${path}_${(index + 1).toString().padStart(2, '0')}.png`,
                key: `${gender}-${path}-${index + 1}`,
                value: `${path}`,
                index: index,
            }));
        }
    },
};

export default FJHeadshotUtil;
